/* istanbul ignore file */
/* tslint:disable */
 
import type { CheckConnectionResponse } from '../models/CheckConnectionResponse';
import type { ECecInstance } from '../models/ECecInstance';
import type { ECecInstanceCreate } from '../models/ECecInstanceCreate';
import type { ECecInstanceDetail } from '../models/ECecInstanceDetail';
import type { ExternalApi } from '../models/ExternalApi';
import type { PaginatedAvailableCashBoxList } from '../models/PaginatedAvailableCashBoxList';
import type { PaginatedECecInstanceListList } from '../models/PaginatedECecInstanceListList';
import type { PaginatedExternalApiList } from '../models/PaginatedExternalApiList';
import type { PaginatedSettingList } from '../models/PaginatedSettingList';
import type { PatchedECecInstance } from '../models/PatchedECecInstance';
import type { PatchedExternalApi } from '../models/PatchedExternalApi';
import type { PatchedSetting } from '../models/PatchedSetting';
import type { SelectCashBox } from '../models/SelectCashBox';
import type { SelectCashBoxResponse } from '../models/SelectCashBoxResponse';
import type { Setting } from '../models/Setting';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class SettingsService {

  /**
   * @param ordering Ordering fields: title, status, created, modified
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @returns PaginatedECecInstanceListList 
   * @throws ApiError
   */
  public static settingsEcecInstancesList(
ordering?: string,
page?: number,
pageSize?: number,
): CancelablePromise<PaginatedECecInstanceListList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/settings/ecec-instances/',
      query: {
        'ordering': ordering,
        'page': page,
        'page_size': pageSize,
      },
    });
  }

  /**
   * @param requestBody 
   * @returns ECecInstanceCreate 
   * @throws ApiError
   */
  public static settingsEcecInstancesCreate(
requestBody: ECecInstanceCreate,
): CancelablePromise<ECecInstanceCreate> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/settings/ecec-instances/',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param id A unique integer value identifying this ECEc Instance.
   * @returns ECecInstanceDetail 
   * @throws ApiError
   */
  public static settingsEcecInstancesRetrieve(
id: number,
): CancelablePromise<ECecInstanceDetail> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/settings/ecec-instances/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this ECEc Instance.
   * @param requestBody 
   * @returns ECecInstance 
   * @throws ApiError
   */
  public static settingsEcecInstancesPartialUpdate(
id: number,
requestBody?: PatchedECecInstance,
): CancelablePromise<ECecInstance> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/settings/ecec-instances/{id}/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param id A unique integer value identifying this ECEc Instance.
   * @returns void 
   * @throws ApiError
   */
  public static settingsEcecInstancesDestroy(
id: number,
): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/settings/ecec-instances/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * Verificare manuală conexiune
   * @param id A unique integer value identifying this ECEc Instance.
   * @param requestBody 
   * @returns CheckConnectionResponse 
   * @throws ApiError
   */
  public static settingsEcecInstancesCheckConnectionCreate(
id: number,
requestBody: ECecInstance,
): CancelablePromise<CheckConnectionResponse> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/settings/ecec-instances/{id}/check_connection/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param id A unique integer value identifying this ECEc Instance.
   * @param ordering Ordering fields: title, status, created, modified
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @returns PaginatedAvailableCashBoxList 
   * @throws ApiError
   */
  public static settingsEcecInstancesGetAvailableCashBoxesList(
id: number,
ordering?: string,
page?: number,
pageSize?: number,
): CancelablePromise<PaginatedAvailableCashBoxList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/settings/ecec-instances/{id}/get_available_cash_boxes/',
      path: {
        'id': id,
      },
      query: {
        'ordering': ordering,
        'page': page,
        'page_size': pageSize,
      },
    });
  }

  /**
   * Selectare casă când sunt multiple case disponibile
   * @param id A unique integer value identifying this ECEc Instance.
   * @param requestBody 
   * @returns SelectCashBoxResponse 
   * @throws ApiError
   */
  public static settingsEcecInstancesSelectCashBoxCreate(
id: number,
requestBody: SelectCashBox,
): CancelablePromise<SelectCashBoxResponse> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/settings/ecec-instances/{id}/select_cash_box/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param active 
   * @param ordering Ordering fields: type, active, created, modified
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param type * `rca` - Rca
 * * `maib` - Maib
 * * `maib_mia` - Maib Mia
 * * `test_payment` - Test Payment
 * * `e_cec` - E Cec
 * * `one_c` - One C
 * * `maib_new` - Maib New
   * @returns PaginatedExternalApiList 
   * @throws ApiError
   */
  public static settingsExternalApisList(
active?: boolean,
ordering?: string,
page?: number,
pageSize?: number,
type?: 'e_cec' | 'maib' | 'maib_mia' | 'maib_new' | 'one_c' | 'rca' | 'test_payment',
): CancelablePromise<PaginatedExternalApiList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/settings/external-apis/',
      query: {
        'active': active,
        'ordering': ordering,
        'page': page,
        'page_size': pageSize,
        'type': type,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this external api.
   * @param requestBody 
   * @returns ExternalApi 
   * @throws ApiError
   */
  public static settingsExternalApisPartialUpdate(
id: number,
requestBody?: PatchedExternalApi,
): CancelablePromise<ExternalApi> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/settings/external-apis/{id}/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param ordering Ordering fields: name, description
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param type * `int` - Int
 * * `float` - Float
 * * `string` - String
 * * `decimal` - Decimal
 * * `list` - List
 * * `date` - Date
 * * `time` - Time
   * @returns PaginatedSettingList 
   * @throws ApiError
   */
  public static settingsSettingsList(
ordering?: string,
page?: number,
pageSize?: number,
type?: 'date' | 'decimal' | 'float' | 'int' | 'list' | 'string' | 'time',
): CancelablePromise<PaginatedSettingList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/settings/settings/',
      query: {
        'ordering': ordering,
        'page': page,
        'page_size': pageSize,
        'type': type,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this setting.
   * @returns Setting 
   * @throws ApiError
   */
  public static settingsSettingsRetrieve(
id: number,
): CancelablePromise<Setting> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/settings/settings/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this setting.
   * @param requestBody 
   * @returns Setting 
   * @throws ApiError
   */
  public static settingsSettingsPartialUpdate(
id: number,
requestBody?: PatchedSetting,
): CancelablePromise<Setting> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/settings/settings/{id}/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

}
